/**
 * Reservation API service. Public endpoints (check, submit) use slug; owner endpoints use restaurant uuid.
 * See docs/API-REFERENCE.md Table reservations.
 */

import api, { normalizeApiError } from './api'
import { verificationService } from './verification.service'

/**
 * GET /api/public/restaurants/{slug}/reservations/check — optional date (Y-m-d), time (H:i).
 * @param {string} slug - Restaurant slug
 * @param {{ date?: string, time?: string }} params
 * @returns {Promise<{ reservations_open: boolean, message?: string | null }>}
 */
export async function check(slug, params = {}) {
  const { data } = await api.get(`/public/restaurants/${encodeURIComponent(slug)}/reservations/check`, {
    params: { date: params.date, time: params.time },
    skipAuth: true,
  })
  return data
}

/**
 * Request SMS verification code for reservation. Delegates to generic verification service.
 * @param {string} slug - Restaurant slug
 * @param {{ customer_mobile: string }} payload - +country code phone number
 * @returns {Promise<{ message: string, verification_token: string }>}
 */
export async function requestCode(slug, { customer_mobile }) {
  return verificationService.requestCode({ purpose: 'reservation', scope: slug, customer_mobile })
}

/**
 * POST /api/public/restaurants/{slug}/reservations — submit reservation (no auth).
 * Requires prior SMS verification: call requestCode, then pass verification_token and verification_code from user.
 * @param {string} slug - Restaurant slug
 * @param {{
 *   pax: number,
 *   date: string,
 *   time: string,
 *   customer_name: string,
 *   customer_mobile: string,
 *   verification_token: string,
 *   verification_code: string
 * }} body - Reservation fields plus verification_token (from request-code) and verification_code (6 digits from SMS)
 * @returns {Promise<{ message: string, data: object }>}
 */
export async function submit(slug, body) {
  const { data } = await api.post(`/public/restaurants/${encodeURIComponent(slug)}/reservations`, body, {
    skipAuth: true,
  })
  return data
}

/**
 * GET /api/restaurants/{restaurantUuid}/reservations — list (owner, Bearer + table_reservation).
 * @param {string} restaurantUuid
 * @param {{ status?: string, date_from?: string, date_to?: string }} params
 * @returns {Promise<{ data: object[] }>}
 */
export async function list(restaurantUuid, params = {}) {
  const { data } = await api.get(`/restaurants/${encodeURIComponent(restaurantUuid)}/reservations`, {
    params: {
      status: params.status,
      date_from: params.date_from,
      date_to: params.date_to,
    },
  })
  return data
}

/**
 * POST /api/restaurants/{restaurantUuid}/reservations — create reservation (owner, Bearer + table_reservation).
 * @param {string} restaurantUuid
 * @param {{ booking_date: string, booking_time: string, customer_name: string, customer_mobile: string, pax: number, status?: 'pending'|'approved' }} body
 * @returns {Promise<{ message: string, data: object }>}
 */
export async function create(restaurantUuid, body) {
  const { data } = await api.post(
    `/restaurants/${encodeURIComponent(restaurantUuid)}/reservations`,
    body
  )
  return data
}

/**
 * PATCH /api/restaurants/{restaurantUuid}/reservations/{reservationUuid} — status (approved|rejected|cancelled) or edit booking_date, booking_time, pax.
 * @param {string} restaurantUuid
 * @param {string} reservationUuid
 * @param {{ status?: 'approved'|'rejected'|'cancelled', booking_date?: string, booking_time?: string, pax?: number }} body
 * @returns {Promise<{ message: string, data: object }>}
 */
export async function update(restaurantUuid, reservationUuid, body) {
  const { data } = await api.patch(
    `/restaurants/${encodeURIComponent(restaurantUuid)}/reservations/${encodeURIComponent(reservationUuid)}`,
    body
  )
  return data
}

/**
 * DELETE /api/restaurants/{restaurantUuid}/reservations/{reservationUuid} — delete reservation (owner, Bearer + table_reservation).
 * Permanently removes the reservation; no status change, no SMS.
 * @param {string} restaurantUuid
 * @param {string} reservationUuid
 * @returns {Promise<{ message: string }>}
 */
export async function remove(restaurantUuid, reservationUuid) {
  const { data } = await api.delete(
    `/restaurants/${encodeURIComponent(restaurantUuid)}/reservations/${encodeURIComponent(reservationUuid)}`
  )
  return data
}

/** @deprecated Use update() with { status } instead. */
export async function updateStatus(restaurantUuid, reservationUuid, body) {
  return update(restaurantUuid, reservationUuid, body)
}

export const reservationService = {
  check,
  requestCode,
  submit,
  list,
  create,
  update,
  remove,
  updateStatus,
}

export default reservationService
